/**
 * Reusable loading spinner component with different sizes and styles
 */
import React from 'react';

export interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
  color?: 'blue' | 'gray' | 'white';
  text?: string;
  fullScreen?: boolean;
}

const sizeClasses = {
  small: 'h-4 w-4',
  medium: 'h-8 w-8',
  large: 'h-12 w-12'
};

const colorClasses = {
  blue: 'border-blue-500',
  gray: 'border-gray-500',
  white: 'border-white'
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'medium',
  className = '',
  color = 'blue',
  text,
  fullScreen = false
}) => {
  const spinner = (
    <div className={`inline-flex items-center ${className}`}>
      <div
        className={`
          animate-spin rounded-full border-2 border-t-transparent
          ${sizeClasses[size]}
          ${colorClasses[color]}
        `}
      />
      {text && (
        <span className={`ml-2 text-sm ${color === 'white' ? 'text-white' : 'text-gray-600'}`}>
          {text}
        </span>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
        <div className="text-center">
          {spinner}
        </div>
      </div>
    );
  }

  return spinner;
};

/**
 * Loading container for page-level loading states
 */
export const LoadingContainer: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => (
  <div className="flex justify-center items-center py-12">
    <LoadingSpinner size="large" text={text} />
  </div>
);

/**
 * Inline loading for buttons and small components
 */
export const InlineLoading: React.FC<{ text?: string }> = ({ text }) => (
  <LoadingSpinner size="small" text={text} className="mr-2" />
);

/**
 * Loading overlay for forms and data tables
 */
export const LoadingOverlay: React.FC<{ isLoading: boolean; children: React.ReactNode }> = ({
  isLoading,
  children
}) => (
  <div className="relative">
    {children}
    {isLoading && (
      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
        <LoadingSpinner size="medium" />
      </div>
    )}
  </div>
);

export default LoadingSpinner;